import { ObjectId } from 'bson';
import { ConsoleWebhookPayload } from './services/console/console-compliance-types';
import { ValueOf } from './utils/helper';

export enum Tables {
  Orders = 'Orders',
  ServiceOrders = 'ServiceOrders',
  Users = 'Users',
  Clients = 'Clients',
  Contractors = 'Contractors',
  Sequences = 'Sequences',
  InspectionModels = 'InspectionModels',
  InspectionData = 'InspectionData',
  Comments = 'Comments',
  Tags = 'Tags',
  SmsHistories = 'SmsHistories',
  KpiHistories = 'KpiHistories',
  BonusHistories = 'BonusHistories',
  FinancialYears = 'FinancialYears',
  Properties = 'Properties',
  ExternalAccounts = 'ExternalAccounts',
  NotificationSchedules = 'NotificationSchedules',
  SnapInspections = 'SnapInspections',
  AddressMatchRules = 'AddressMatchRules',
  SyncingEventHistories = 'SyncingEventHistories',
  Companies = 'Companies',
  SharedNotificationLinks = 'SharedNotificationLinks',
  ConsoleWebhookEvents = 'ConsoleWebhookEvents'
}

export interface Notification {
  time: Date;
  notified?: boolean;
  notifiedAt?: Date;
}

enum NotificationMethod {
  SMS = 'SMS',
  Email = 'Email'
}

type SyncJobKeys = 'SA' | 'HHA' | 'MTLD';

export interface SyncSettings {
  repeatIntervalDays?: number;
  repeatPrice?: number;
  normalPrice?: number;
  packagePrice?: number;
  syncEnabled?: boolean;
  onlyForPreviousOrders?: boolean;
}
export interface ExternalAccount {
  _id: ObjectId;
  /** Palace only — a ConsoleCloud office authenticates with its API key instead. */
  username?: string;
  password?: string;
  type: 'Palace' | 'ConsoleCloud';
  /** Console Cloud compliance integration for this office. See ConsoleCloudAccount. */
  consoleCloud?: ConsoleCloudAccount;
  linkedEmail: string;
  mobile?: string;
  lastSyncedAt?: Date;
  contactName?: string;
  company?: string;
  createdAt: Date;
  updatedAt?: Date;
  onlyNotifyAccountManager?: boolean;
  agentEmailsToNotify?: string[];
  syncFromSnapInspect?: boolean;
  syncSettings?: Record<SyncJobKeys, SyncSettings>;
  skipAnnualConfirmSettings?: {
    type: 'Account' | 'Agents';
    agentEmails: string[];
  };
}
export interface NotificationSchedule {
  _id: ObjectId;
  notifyId: string;
  retrieveId?: string;
  type: 'SA' | 'HHA';
  fullAddress: string;
  contactName: string;
  email?: string;
  mobile?: string;
  lastCheckedTime: Date;
  notifies: Notification[];
  methods: NotificationMethod[];
  isAgent: boolean;
  externalAccountId?: ObjectId;
  isAnnualRepeat: boolean;
  lastOrderNumber?: string;
  active: boolean;
  propertyId?: string;
  createdAt: Date;
  newOrderId?: ObjectId;
  clientId?: ObjectId;
  hasCancelled?: boolean;
  skipExpireDate?: boolean;
  isInterimCheck?: boolean;
}

export interface SharedNotificationRecipient {
  email: string;
  name: string;
  role: 'Manager' | 'Agent';
  passcode: string;
  passcodeExpiresAt: Date;
  lastResentAt?: Date;
}

export interface SharedNotificationLink {
  _id: ObjectId;
  token: string;
  externalAccountId: ObjectId;
  recipients: SharedNotificationRecipient[];
  createdAt: Date;
}

export interface FinancialYear {
  _id: ObjectId;
  year: string;
  month: number;
}

export enum UserRoles {
  Admin = 'Admin',
  Inspector = 'Inspector',
  Auditor = 'Auditor',
  SuperAdmin = 'SuperAdmin'
}

export enum OrderStatus {
  ContactPending = 'ContactPending',
  ContactSupplied = 'ContactSupplied',
  AgreementPending = 'AgreementPending',
  AgreementSupplied = 'AgreementSupplied',
  Ready = 'Ready',
  InProgress = 'InProgress',
  Completed = 'Completed',
  Cancelled = 'Cancelled'
}

export enum ServiceOrderStatus {
  ContactPending = 'ContactPending',
  ContactSupplied = 'ContactSupplied',
  AgreementPending = 'AgreementPending',
  AgreementSupplied = 'AgreementSupplied',
  Quoted = 'Quoted',
  PrePaymentPending = 'PrePaymentPending',
  Ready = 'Ready',
  InProgress = 'InProgress',
  Cancelled = 'Cancelled',
  FinalPaymentPending = 'FinalPaymentPending',
  Completed = 'Completed'
}

export enum JobStatus {
  Draft = 'Draft',
  Ready = 'Ready',
  Assigned = 'Assigned',
  InRevisal = 'InRevisal',
  Completed = 'Completed',
  Audited = 'Audited',
  ReportSent = 'ReportSent',
  /**
   * Set when a compliance item is cancelled at source — the job stays on the
   * order as a record that it was asked for and withdrawn. Excluded from the
   * order-level "all jobs done" checks and from the KPI allowlist, so a cancelled
   * job neither blocks an order from completing nor gets paid.
   */
  Cancelled = 'Cancelled'
}

export enum ServiceJobStatus {
  Draft = 'Draft',
  Accepted = 'Accepted',
  Cancelled = 'Cancelled',
  Ready = 'Ready',
  Completed = 'Completed'
}

export enum SequenceType {
  Order = 'Order',
  Service = 'Service',
  TestOrder = 'TestOrder',
  TestServiceOrder = 'TestServiceOrder'
}

export enum OrderTypes {
  Inspection = 'Inspection',
  Service = 'Service'
}

export enum CommentsCategories {
  SmokeAlarm = 'SmokeAlarm',
  MethReport = 'Meth',
  BuildingInspection = 'BuildingInspection'
}

export type BuildingInspection = {
  type: 'BuildingInspectionVerbal' | 'BuildingInspectionWritten';
  isApartment?: boolean;
  category?: 'PrePurchase' | 'PreSale' | 'WeatherTightness' | 'PreDelivery' | 'Maintenance' | 'ApartmentInterior';
  shortKey: 'BIW' | 'BIV';
} & BaseJob;

export type MethTesting = {
  shortKey: 'MTID' | 'MTLD' | 'MTIFC' | 'MTLFC' | 'MTCV' | 'MTCW' | 'CMW' | 'CMV';
  type:
    | 'MethTestIndividualDiscrete'
    | 'MethTestLabDiscrete'
    | 'MethTestIndividualFieldComposite'
    | 'MethTestLabFieldComposite'
    | 'Check4MethWritten'
    | 'Check4MethVerbal';
  docTypes: ['LabReport'];
} & BaseJob;

export type Check4MethVerbal = {
  shortKey: 'MTC4MV';
  type: 'Check4MethVerbal';
  docTypes: [];
} & BaseJob;

type SafeSanitary = {
  shortKey: 'SS';
  type: 'SafeSanitaryCheck';
  docTypes: ['Report'];
} & BaseJob;

type HealthyHomesAssessment = {
  shortKey: 'HHA';
  type: 'HealthyHomesAssessment';
  docTypes: ['ComplianceReport', 'HeatingReport'];
} & BaseJob;

type SmokeAlarmAssessment = {
  shortKey: 'SA';
  type: 'SmokeAlarmAssessment';
  docTypes: ['Report'];
} & BaseJob;

type InsulationAssessment = {
  shortKey: 'IA';
  type: 'InsulationAssessment';
  docTypes: ['Report'];
} & BaseJob;

type HeatingAssessment = {
  shortKey: 'HA';
  type: 'HeatingAssessment';
  docTypes: ['Report'];
} & BaseJob;

type AsbestosTesting = {
  shortKey: 'AT';
  type: 'AsbestosTesting';
  docTypes: ['Report'];
} & BaseJob;

type SwimmingPoolSafetyInspection = {
  shortKey: 'SP';
  type: 'SwimmingPoolSafetyInspection';
  docTypes: ['Report'];
} & BaseJob;

type ConditionSurveyReport = {
  shortKey: 'CS';
  type: 'ConditionSurveyReport';
  docTypes: ['Report'];
} & BaseJob;

type IngoingConditionReport = {
  shortKey: 'IC';
  type: 'IngoingConditionReport';
  docTypes: ['Report'];
} & BaseJob;

export type ConsultancyService = {
  shortKey: 'CSW' | 'CSV';
  type: 'ConsultancyServiceVerbal' | 'ConsultancyServiceWritten';
  docTypes: ['Report'];
} & BaseJob;

export type PostInspectionService = {
  shortKey: 'PISW' | 'PISV';
  type: 'PostInspectionServiceVerbal' | 'PostInspectionServiceWritten';
  docTypes: ['Report'];
} & BaseJob;

export const AllJobTypes = [
  'BuildingInspectionVerbal',
  'BuildingInspectionWritten',
  'MethTestIndividualDiscrete',
  'MethTestLabDiscrete',
  'MethTestIndividualFieldComposite',
  'MethTestLabFieldComposite',
  'Check4MethWritten',
  'Check4MethVerbal',
  'SafeSanitaryCheck',
  'HealthyHomesAssessment',
  'SmokeAlarmAssessment',
  'InsulationAssessment',
  'HeatingAssessment',
  'AsbestosTesting',
  'SwimmingPoolSafetyInspection',
  'ConditionSurveyReport',
  'IngoingConditionReport',
  'ConsultancyService',
  'ConsultancyServiceVerbal',
  'PostInspectionServiceVerbal',
  'PostInspectionServiceWritten'
] as const;

export type Job =
  | BuildingInspection
  | MethTesting
  | SafeSanitary
  | HealthyHomesAssessment
  | SmokeAlarmAssessment
  | InsulationAssessment
  | HeatingAssessment
  | AsbestosTesting
  | SwimmingPoolSafetyInspection
  | ConditionSurveyReport
  | IngoingConditionReport
  | ConsultancyService
  | PostInspectionService;

export interface Contractor {
  _id: ObjectId;
  company: string;
  name: string;
  mobile: string;
  email: string;
  bankAccount: string;
}

export interface StaffRatio {
  userId: string;
  email: string;
  mobile?: string;
  userName: string;
  ratio: number;
}

export interface ContractPayment {
  invoiceNumber?: string;
  paidAt?: Date;
  operator?: string;
}
export interface ServiceJob {
  jobId: string;
  title: string;
  description?: string;
  status: ServiceJobStatus;
  schedule?: Date;
  photos?: S3Object[];
  docs?: Record<string, Doc>;
  contractor: Contractor & ContractPayment;
  priceAdjustments?: {
    changeInCents: number;
    at: Date;
    userName: string;
    target: 'AdditionalCost' | 'ContractorQuote' | 'WorkPrice';
  }[];
  contractorQuoteInCents: number;
  additionalCostInCents?: number;
  costDescription?: string;
  totalCostInCents: number;
  workPriceInCents: number;
  staffRatios: StaffRatio[];
  contractorRemark?: string;
  workFiles: S3Object[];
  hasSentWorkOrder?: boolean;
  prepaidAmountInCents?: number;
  finalPaymentInCents?: number;
  docsSent?: boolean;
}

export interface Address {
  memo?: string;
  fullAddress: string;
  city?: string;
  suburb?: string;
  postcode?: string;
  location?: [number, number]; // [longitude, latitude]
  skipAnnualConfirm?: boolean;
}

export interface TenantDetail {
  name: string;
  mobile: string;
  email?: string;
  doorCode?: string;
  gateCode?: string;
  alarmCode?: string;
  keyLocation?: string;
  remark?: string;
}

export interface S3Object {
  template?: string;
  bucket: string;
  key: string;
}

export interface User {
  _id: ObjectId;
  firstName: string;
  lastName: string;
  password: string;
  email: string;
  securityEmail?: string;
  mobile?: string;
  role: UserRoles;
  salary?: number;
  bonusRatio?: number;
  signatureId?: string;
  sessionId?: string;
  disabled?: boolean;
  isInspector?: boolean;
  baseGeolocation?: [number, number];
  updatedAt?: Date;
  createdAt?: Date;
}

export interface Client {
  _id: ObjectId;
  vip?: boolean;
  firstName: string;
  middleName?: string;
  lastName?: string;
  company?: string;
  email?: string;
  mobile: string;
  type: 'Agent' | 'Customer';
  addresses?: Address[];
}

export interface InspectionOrder extends BaseOrder {
  type: OrderTypes.Inspection;
  jobs: Job[];
  status: OrderStatus;
  invoiceTo?: string;
  invoiceEmail?: string;
  ccEmails?: string[];
  tenants?: TenantDetail[];
  parentOrder?: ObjectId;
  childOrders?: ObjectId[];
  paid?: boolean;
  paidAt?: Date;
  invoiceNumber?: string;
  totalQuote?: number;
  reportQuotes?: {
    title: string;
    price: number;
  }[];
  paymentReceivedAt?: Date;
  referralRate?: number;
  referralBank?: string;
  updatedAt?: Date;
  auditCompleted?: boolean;
  invoiceIssuedAt?: Date;
  archive?: string;
  externalAccountId?: ObjectId;
  externalPropertyId?: string;
  lastOrderNumber?: string;
  syncedToExternalSource?: boolean;
  lastSyncedAt?: Date;
  /** Set when the order was opened from a Console Cloud compliance request. */
  consoleCompliance?: ConsoleComplianceOrderLink;
}

/**
 * Links an inspection order back to the Console Cloud compliance requests behind
 * it. One order aggregates every item the PM assigned us on that property, so an
 * inspector does them in one visit — see `services/console/console-compliance-order.ts`.
 */
export interface ConsoleComplianceOrderLink {
  officeId: string;
  propertyId: string;
  environment: ConsoleEnvironment;
  /**
   * `<environment>:<officeId>:<propertyId>`, carrying a unique index — it is what
   * makes two compliance events arriving at once produce one order instead of two.
   *
   * Present only while the order still accepts new compliance jobs. Once it
   * leaves an open status the next request clears this field and starts a fresh
   * order, so nothing has to hook every place an order's status changes.
   */
  activeGroupKey?: string;
  /** Every `propertyComplianceProcessId` on this order. Guards against re-adding a job. */
  processIds: string[];
  /** The event that opened the order, for tracing back to `ConsoleWebhookEvents`. */
  firstEventId: string;
  /**
   * The `lastUpdatedDateTime` of the newest Console event applied here. Console
   * does not guarantee delivery order, so an event older than this is a delayed
   * retry that has been overtaken and must not be applied.
   */
  lastEventDateTime?: Date;
  /** Processes Console has cancelled. Kept so a redelivered cancel is a no-op. */
  cancelledProcessIds?: string[];
  createdAt: Date;
  updatedAt?: Date;
}

export interface Referral {
  fullName: string;
  company: string;
  mobile: string;
  email: string;
  bankAccount: string;
}

export interface BaseOrder {
  _id: ObjectId;
  archive?: string;
  docSignId?: string;
  orderNumber: string;
  invoiceNo?: string;
  clientId: ObjectId;
  client: Pick<Client, 'firstName' | 'lastName' | 'middleName' | 'company' | 'email' | 'mobile' | 'type'>;
  createdAt: Date;
  preferredDate?: Date;
  address?: Address;
  referral?: Referral;
  invoiceTo?: string;
  invoiceEmail?: string;
  ccEmails?: string[];
  skipContactForm?: boolean;
  skipAgreement?: boolean;
  sentReport?: boolean;
  signingDocuments?: {
    [key: string]: S3Object | null;
  };
  lastUpdatedAt?: Date;
  kpiCalculated: boolean;
  isAnnualRepeat?: boolean;
  company?: string;
  clientReference?: string;
}

export interface ServiceOrder extends BaseOrder {
  type: OrderTypes.Service;
  prepayRatio: number;
  status: ServiceOrderStatus;
  docSignId?: string;
  jobs?: ServiceJob[];
  address?: Address;
  tenants?: TenantDetail[];
  parentOrder?: ObjectId | undefined;
  childOrders?: ObjectId[];
  clientRemark?: string;
  prepayment?: {
    invoiceNumber?: string;
    issuedAt?: Date;
    paidAt?: Date;
    paid: boolean;
    amountInCent?: number;
  };
  finalPayment?: {
    invoiceNumber?: string;
    issuedAt?: Date;
    paidAt?: Date;
    amountInCent: number;
    paid: boolean;
  };
}

export interface Sequence {
  _id: ObjectId;
  type: string;
  year: number;
  lastSeq: number;
}

export interface Doc {
  file?: S3Object;
  type: 'PDF';
  createdAt: Date;
  category?: string;
  status: 'Pending' | 'Available';
}

export interface BaseJob {
  jobId: string;
  shortKey: string;
  auditor?: User['_id'];
  auditedAt?: Date;
  inspector?: User['_id'];
  schedule?: Date;
  quote?: number;
  docs?: Record<string, Doc>;
  labResults?: Record<string, Doc>;
  docTypes?: string[];
  status: JobStatus;
  description?: string;
  ref?: string;
  durationHours?: number;
  docsSent?: boolean;
  /**
   * `propertyComplianceProcessId` when this job came from a Console Cloud
   * compliance request. It is how a later PROPERTY_COMPLIANCE_UPDATED or
   * _CANCELLED event finds the one job it refers to.
   */
  consoleProcessId?: string;
  /**
   * Console's `lastInspectionDate` for this compliance item — when the property
   * was last checked for it, as the managing office understands it. Console's
   * figure, not ours: it can predate any QHI job, since the office may have used
   * another provider before us.
   */
  consoleLastCheckedAt?: Date;
  category?: keyof typeof BuildingCategories;
  staffRatios?: StaffRatio[];
  costInCents?: number;
  costDescription?: string;
  marginInCents?: number;
  annualNotifyDisabled?: boolean;
  isInterimCheck?: boolean;
}

export type JobTypes = Pick<Job, 'type'>;

export interface InspectionModel {
  type: JobTypes;
  model: Object;
  updatedAt?: Date;
  createdAt: Date;
}

export interface Comment {
  _id: ObjectId;
  title: string;
  content: string;
  category: CommentsCategories;
  tags: string[];
}

export interface Tag {
  _id: ObjectId;
  category: string;
  tag: string;
}

export interface InspectionData {
  _id: ObjectId;
  data: Record<string, any>;
  jobType: ValueOf<JobTypes>;
  jobId: string;
  pages: PageConfig[];
  inspector: User['_id'];
  createdAt: Date;
  updatedAt: Date;
}

export interface InspectionModel {
  _id: ObjectId;
  type: JobTypes;
  model: Object;
  createdAt: Date;
  updatedAt?: Date;
}

export interface PageConfig {
  id: string;
  isHidden: boolean;
  index: number;
}

export interface Event {
  id: string;
  title: string;
  allDay?: string;
  start?: string;
  end?: string;
}

export interface SmsHistory {
  _id: ObjectId;
  to: string;
  content: string;
  result: any;
}

export interface JobKpiHistory {
  jobId: string;
  ratio: number;
  isComplete: boolean;
  totalCost: number;
  workPrice: number;
  kpi: number;
}

export interface KPIHistory {
  _id: ObjectId;
  month: number;
  userId: string;
  email: string;
  staffName: string;
  totalKpi: number;
  totalIncompleteKpi: number;
  jobs: JobKpiHistory[];
  createdAt: Date;
}

export enum BuildingCategories {
  PrePurchase = 'Pre-Purchase',
  PreSale = 'Pre-Sale',
  WeatherTightness = 'Weather Tightness',
  PreDelivery = 'Pre-Delivery',
  Maintenance = 'Maintenance',
  ApartmentInterior = 'Apartment Interior'
}

export interface BonusHistory {
  _id: ObjectId;
  userId: string;
  staffName: string;
  endMonth: number;
  bonusRatio: number;
  salary: number;
  totalCompletedKpi: number;
  bonus: number;
  updatedAt: Date;
}

export interface AddressMatchRule {
  _id: ObjectId;
  externalAccountId: ObjectId;
  externalPropertyId: string;
  externalAccountType: 'Palace';
  externalAddress: string;
  orderNumbers?: string[];
  orderAddresses?: string[];
  snapAddress?: string;
  snapInspectionId?: SnapInspection['inspectionId'];
  snapAccountId?: SnapInspection['accountId'];
  createdAt: Date;
  manualVerified?: boolean;
  skipAnnualConfirm?: boolean;
  // Left it to 'Manual' when manualResolution is 'New'
  // Pending: wait for auto address matching
  // Matched: matched with an order and/or snap inspection
  // Manual: require manual resolution
  // Ignore: ignore this address
  matchingStatus: 'Matched' | 'Manual' | 'Ignore' | 'Pending';
  // needs update matchingStatus to 'Order' or 'Snap' when manualResolution is 'Order' or 'Snap'
  matchingResolution?: 'Order' | 'Snap' | 'Order-Snap' | 'New' | 'Ignore';
  updatedAt?: Date;
}

export interface Property {
  _id: ObjectId;
  accountId: ObjectId;
  propertyId: string;
  unit?: string;
  addressLine1: string;
  addressLine2?: string;
  addressLine3?: string;
  addressLine4?: string;
  fullAddress: string;
  agentEmail?: string;
  agentFullName?: string;
  postCode: string;
  status: 'ACTIVE' | 'INACTIVE';
  smokeAlarmLastCheckedAt?: Date;
  smokeAlarmLastCheckedBy?: string;
  healthHomeFinalCompliantAt?: Date;
  missingOriginalSmokeAlarmLastCheckedAt?: boolean;
  missingOriginalHealthHomeFinalCompliantAt?: boolean;
  createdAt: Date;
  updatedAt?: Date;
  skipAnnualCheck?: boolean;
}

export interface SnapInspection {
  _id: ObjectId;
  inspectionId: number;
  accountId: string;
  assetId: number;
  orderNumber?: string;
  fullAddress: string;
  geoLocation?: [number, number];
  startDate?: Date;
  endDate?: Date;
  createdAt: Date;
  updatedAt?: Date;
  jobNumber?: string;
  docs: {
    [key: string]: {
      category: 'Healthy Home Report' | 'Heating Report';
      createdAt: Date;
      file: S3Object;
    };
  };
  heating?: {
    source: string;
    compliantStatus: string;
    totalHeatCapacity: string;
    requiredHeatCapacity: string;
    comments?: string;
    fireplace?: string;
  };
  ceilInsulation?: {
    compliantStatus: string;
    insulationType: string;
    thickness: string;
    comments?: string;
    insulationDate?: string;
  };
  floorInsulation?: {
    compliantStatus: string;
    insulationType: string;
    thickness?: string;
    comments?: string;
    insulationDate?: string;
  };
  kitchens?: {
    exhaustFan: string;
    comments?: string;
    gapCompliantStatus: string;
  }[];
  bathrooms?: {
    compliantStatus: string;
    exhaustFan: string;
    comments?: string;
    gapCompliantStatus: string;
  }[];
  toilets?: {
    compliantStatus: string;
    ventilationType: string;
    comments?: string;
    gapCompliantStatus: string;
  }[];
  laundries?: {
    compliantStatus: string;
    ventilationType: string;
    comments?: string;
    gapCompliantStatus: string;
  }[];
  bedrooms?: {
    compliantStatus: string;
    ventilationType: string;
    comments?: string;
    gapCompliantStatus: string;
  }[];
  livingRooms?: {
    compliantStatus: string;
    ventilationType: string;
    comments?: string;
    gapCompliantStatus: string;
  }[];
  dinningRooms?: {
    compliantStatus: string;
    ventilationType: string;
    comments?: string;
    gapCompliantStatus: string;
  }[];
  entries?: {
    compliantStatus: string;
    ventilationType: string;
    comments?: string;
    gapCompliantStatus: string;
  }[];
  hallways?: {
    compliantStatus: string;
    ventilationType: string;
    comments?: string;
    gapCompliantStatus: string;
  }[];
  conservatories?: {
    compliantStatus: string;
    ventilationType: string;
    comments?: string;
    gapCompliantStatus: string;
  }[];
  drainageAndGuttering?: {
    compliantStatus: string;
    comments?: string;
  };
  groundMoistureAndDrainage?: {
    compliantStatus: string;
    foundationType: string;
    comments?: string;
  };
}


export enum SyncingEventActionNames {
  SyncQHIOrderToPalace = 'SyncQHIOrderToPalace',
  SyncNZHHSInspectionToPalace = 'SyncNZHHSInspectionToPalace',
}

export interface SyncingEventHistory {
  _id: ObjectId;
  epoch: string; // e.g. '20240901'
  actionName: SyncingEventActionNames;
  externalAccountCompany: string;
  orderNumber?: string;
  hasFailed: boolean;
  actions: {
    name: string;
    localResourceId?: string;
    externalResourceId?: string;
    status: 'Completed' | 'Failed';
    message?: string;
    at: Date;
  }[];
  startAt: Date;
}

export interface Company {
  _id: ObjectId;
  name: string;
  invoiceEmail?: string;
  invoiceTo?: string;
  updatedAt?: Date;
}
/**
 * Console Cloud (Reapit PM) compliance partner integration.
 *
 * The office's API key is pasted in manually by a QHI admin and lives on that
 * office's `ExternalAccount` document under `consoleCloud`. `officeId` comes back
 * from `GET /integration/v1/integrations/_current` once the key is verified, and
 * every later call for that office sends its key in the `API-Key` header.
 */
export enum ConsoleIntegrationStatus {
  Active = 'ACTIVE',
  Unverified = 'UNVERIFIED',
  Inactive = 'INACTIVE'
}

export enum ConsoleEnvironment {
  Sandbox = 'sandbox',
  Production = 'production'
}

export interface ConsoleCloudAccount {
  /**
   * The office's API key, stored and returned as plain text. The admin UI shows it
   * in full so an admin can read back what is saved.
   */
  apiKey: string;
  /** Sandbox and production keys are not interchangeable, so records are scoped. */
  environment: ConsoleEnvironment;
  status: ConsoleIntegrationStatus;
  /** Console's ID for the office. Only known after a successful verify. */
  officeId?: string;
  consoleCreatedDateTime?: Date;
  /** `lastUpdatedDateTime` as reported by Console — used to drop out-of-order events. */
  consoleLastUpdatedDateTime?: Date;
  verifiedAt?: Date;
  deactivatedAt?: Date;
  /** QHI user who pasted the key in. */
  savedBy?: string;
  savedAt: Date;
}

/**
 * Lifecycle of a stored webhook event.
 *
 * Compliance events are not processed in the web app — qhi-site persists them and
 * hands off to qhi-background, so they move Received → Queued → Processing →
 * Processed/Failed/Ignored, with the last three set by the worker. Events handled
 * in-process (INTEGRATION_DEACTIVATED) skip Queued and Processing.
 */
export enum ConsoleWebhookEventStatus {
  Received = 'Received',
  /** Handed to the qhi-background worker; it has not picked it up yet. */
  Queued = 'Queued',
  /** Claimed by a worker invocation. */
  Processing = 'Processing',
  Processed = 'Processed',
  Failed = 'Failed',
  Ignored = 'Ignored'
}

export interface ConsoleWebhookEvent {
  _id: ObjectId;
  /** Globally unique per Console — carries the unique index that gives us idempotency. */
  eventId: string;
  eventType: string;
  officeId: string;
  environment: ConsoleEnvironment;
  status: ConsoleWebhookEventStatus;
  /**
   * Raw payload, stored verbatim so unknown event types lose nothing. The union
   * describes the event types we have transcribed from Console's OpenAPI
   * documents; anything else stores fine as `ConsoleUnrecognisedWebhookEvent`.
   */
  payload: ConsoleWebhookPayload;
  /** Console's own event timestamp. */
  eventCreatedDateTime?: Date;
  correlationId?: string;
  sourceIp?: string;
  error?: string;
  /**
   * Order this event resolved to, stamped when handling it upserted, updated or
   * cancelled one. Traces an event to the work it produced without scanning
   * Orders by `consoleCompliance.processIds`.
   */
  orderNumber?: string;
  receivedAt: Date;
  /** When the qhi-background worker was kicked off for this event. */
  queuedAt?: Date;
  /** When a worker invocation claimed it. Reset on each attempt. */
  processingStartedAt?: Date;
  /** How many times a worker has claimed this event. */
  attempts?: number;
  processedAt?: Date;
}
